import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import type { SignupPayload, LoginPayload } from '../types';
import {
  applySession,
  clearRoleScope,
  clearSessionStorage,
  initializeAuthSessionManager,
  setRoleScope,
} from '../services/authSession';

interface AuthUser {
  id: string; // Supabase auth user id
  email: string;
  name?: string;
  org_id?: string;
  role?: 'admin' | 'employee' | 'individual' | 'platform_admin';
  userType?: 'org_admin' | 'employee' | 'individual' | 'platform_admin';
  onboarding_complete?: boolean; // Track if user has completed onboarding
  employee_data?: {
    id: string; // employees table id
    name: string;
    org_id: string;
    awareness_score: number;
    risk_level: string;
  };
  individual_data?: {
    id: string; // mobile_users table id
    name: string;
    phone: string | null;
  };
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  isLoading: boolean;
  error: string | null;
  signup: (payload: SignupPayload) => Promise<void>;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  verifyToken: () => Promise<boolean>;
  resendVerificationEmail: (email: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  completePendingSignup: () => Promise<boolean>;
  completeOnboarding: () => Promise<void>; // Mark onboarding as complete
  refreshUserData: () => Promise<void>; // Re-load user data from database
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const mapUserToRoleScope = (authUser: AuthUser | null): string | null => {
  if (!authUser) return null;

  if (authUser.userType) {
    return authUser.userType;
  }

  if (authUser.role === 'admin') {
    return 'org_admin';
  }

  return authUser.role ?? null;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sessionManagerCleanupRef = useRef<(() => void) | null>(null);
  const logoutRef = useRef<() => Promise<void> | null>(null);
  const loadingUserDataRef = useRef(false); // Prevent duplicate loadUserData calls
  const lastLoadTimeRef = useRef(0); // Prevent rapid successive loadUserData calls

  const finalizeUser = useCallback((resolved: AuthUser): AuthUser => {
    setUser(resolved);
    const scope = mapUserToRoleScope(resolved);
    if (scope) {
      setRoleScope(scope);
    } else {
      clearRoleScope();
    }
    return resolved;
  }, []);

  const teardownSessionManager = useCallback(() => {
    if (sessionManagerCleanupRef.current) {
      sessionManagerCleanupRef.current();
      sessionManagerCleanupRef.current = null;
    }
    clearSessionStorage();
  }, []);

  const bootstrapSessionManager = useCallback(
    (activeSession: Session, roleScope: string | null) => {
      applySession(activeSession, roleScope);
      if (!sessionManagerCleanupRef.current) {
        sessionManagerCleanupRef.current = initializeAuthSessionManager({
          onIdleTimeout: () => {
            void logoutRef.current?.();
          },
        });
      }
    },
    [],
  );

  useEffect(() => {
    if (!session) {
      teardownSessionManager();
      return;
    }

    const roleScope = mapUserToRoleScope(user);
    bootstrapSessionManager(session, roleScope);
  }, [session, user, bootstrapSessionManager, teardownSessionManager]);

  useEffect(() => {
    return () => {
      teardownSessionManager();
    };
  }, [teardownSessionManager]);

  // Initialize auth state and listen for changes
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        try {
          await loadUserData(session.user);
        } catch (err) {
          console.error('[AuthContext] Error in loadUserData:', err);
          finalizeUser({
            id: session.user.id,
            email: session.user.email!,
          });
        }
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip INITIAL_SESSION - getSession() above handles the initial load
      if (event === 'INITIAL_SESSION') return;

      setSession(session);

      if (event === 'SIGNED_OUT') {
        setUser(null);
        clearRoleScope();
        return;
      }

      if (session?.user) {
        try {
          // loadUserData has built-in dedup guards (ref + time-based)
          await loadUserData(session.user);
        } catch (err) {
          console.error('[AuthContext] Error in onAuthStateChange loadUserData:', err);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load user profile data from employees table, mobile_users, or platform_admins
  // Uses admin client (service role) to bypass RLS - avoids 406 errors entirely
  // Uses .maybeSingle() instead of .single() to prevent HTTP 406 on 0 rows
  const loadUserData = async (authUser: User): Promise<AuthUser> => {
    // Prevent duplicate concurrent calls AND rapid successive calls (within 2s)
    const now = Date.now();
    if (loadingUserDataRef.current || (now - lastLoadTimeRef.current < 2000 && user?.userType)) {
      return user ?? { id: authUser.id, email: authUser.email! };
    }
    loadingUserDataRef.current = true;
    lastLoadTimeRef.current = now;

    try {
      console.log('[loadUserData] Starting for:', authUser.email);

      // Use admin client for ALL lookups to bypass RLS
      const { getAdminClient } = await import('../lib/supabaseAdmin');
      const adminClient = getAdminClient();

      // 1. Check if user is a platform admin (super admin)
      const { data: platformAdmin, error: paError } = await adminClient
        .from('platform_admins')
        .select('id, email, full_name, permissions, is_active')
        .eq('auth_user_id', authUser.id)
        .maybeSingle();

      if (paError) {
        console.error('[loadUserData] Platform admin query error:', paError);
      }

      if (platformAdmin) {
        console.log('[loadUserData] Platform admin found');
        if (!platformAdmin.is_active) {
          return finalizeUser({ id: authUser.id, email: authUser.email! });
        }
        return finalizeUser({
          id: authUser.id,
          email: authUser.email!,
          name: platformAdmin.full_name,
          role: 'platform_admin',
          userType: 'platform_admin',
          onboarding_complete: true,
        });
      }

      // 2. Check if user is an employee (org admin or regular employee)
      let resolvedEmployee: any = null;

      const { data: employee, error: empError } = await adminClient
        .from('employees')
        .select('id, name, org_id, email, role, is_active, onboarding_complete, awareness_score, risk_level')
        .eq('auth_user_id', authUser.id)
        .maybeSingle();

      if (empError) {
        console.error('[loadUserData] Employee query error:', empError);
      }

      resolvedEmployee = employee;

      // Fallback: try email lookup for legacy rows without auth_user_id
      if (!resolvedEmployee && authUser.email) {
        const { data: byEmail } = await adminClient
          .from('employees')
          .select('id, name, org_id, email, role, is_active, onboarding_complete, awareness_score, risk_level')
          .eq('email', authUser.email)
          .maybeSingle();
        if (byEmail) {
          console.log('[loadUserData] Employee found by email fallback');
          resolvedEmployee = byEmail;
        }
      }

      if (resolvedEmployee) {
        console.log('[loadUserData] Employee found, role:', resolvedEmployee.role);
        if (!resolvedEmployee.is_active) {
          return finalizeUser({ id: authUser.id, email: authUser.email! });
        }
        const userType = resolvedEmployee.role === 'org_admin' ? 'org_admin' : 'employee';
        return finalizeUser({
          id: authUser.id,
          email: authUser.email!,
          org_id: resolvedEmployee.org_id,
          role: resolvedEmployee.role === 'org_admin' ? 'admin' : 'employee',
          userType,
          onboarding_complete: resolvedEmployee.onboarding_complete ?? false,
          employee_data: {
            id: resolvedEmployee.id,
            name: resolvedEmployee.name,
            org_id: resolvedEmployee.org_id,
            awareness_score: resolvedEmployee.awareness_score || 0,
            risk_level: resolvedEmployee.risk_level || 'medium',
          },
        });
      }

      // 3. Check if user is an individual (mobile_users)
      const { data: individualUser, error: indError } = await adminClient
        .from('mobile_users')
        .select('id, name, email, phone, is_active, onboarding_complete')
        .eq('auth_user_id', authUser.id)
        .maybeSingle();

      if (indError) {
        console.error('[loadUserData] Individual query error:', indError);
      }

      if (individualUser) {
        console.log('[loadUserData] Individual user found');
        if (!individualUser.is_active) {
          return finalizeUser({ id: authUser.id, email: authUser.email! });
        }
        return finalizeUser({
          id: authUser.id,
          email: authUser.email!,
          role: 'individual',
          userType: 'individual',
          onboarding_complete: individualUser.onboarding_complete ?? false,
          individual_data: {
            id: individualUser.id,
            name: individualUser.name,
            phone: individualUser.phone,
          },
        });
      }

      // User authenticated but no record found - might be during signup
      console.log('[loadUserData] No user data found in any table, setting minimal user');
      return finalizeUser({
        id: authUser.id,
        email: authUser.email!,
      });
    } catch (err) {
      console.error('[loadUserData] Error:', err);
      return finalizeUser({
        id: authUser.id,
        email: authUser.email!,
      });
    } finally {
      loadingUserDataRef.current = false;
    }
  };

  // Signup function
  const signup = async (payload: SignupPayload) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: payload.admin_email,
        password: payload.admin_password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Signup failed - no user returned');
      
      // Check if email confirmation is required
      if (!authData.session) {
        // Email confirmation required - store signup data for later completion
        localStorage.setItem('pendingSignup', JSON.stringify({
          type: 'org_admin',
          data: {
            org_name: payload.org_name,
            industry: payload.industry,
            company_size: payload.company_size,
            admin_name: payload.admin_name,
            admin_email: payload.admin_email,
            gdpr_consent: payload.gdpr_consent,
            data_processing_agreement: payload.data_processing_agreement,
          },
          user_id: authData.user.id,
        }));
        throw new Error('VERIFICATION_REQUIRED');
      }

      // Clear any pending signup data
      localStorage.removeItem('pendingSignup');

      // CRITICAL: Ensure session is set for RLS to work
      if (authData.session) {
        await supabase.auth.setSession({
          access_token: authData.session.access_token,
          refresh_token: authData.session.refresh_token,
        });
        setSession(authData.session);
        bootstrapSessionManager(authData.session, null);
      }

      // 2. Use admin client (service role) to bypass RLS for signup inserts
      const { getAdminClient } = await import('../lib/supabaseAdmin');
      const adminClient = getAdminClient();

      // Generate a unique 8-char invite code for the organisation
      const inviteCode = Math.random().toString(36).substring(2, 10).toUpperCase();

      // Create organisation
      const { data: orgData, error: orgError } = await adminClient
        .from('organisations')
        .insert({
          name: payload.org_name,
          industry: payload.industry,
          size: payload.company_size,
          owner_id: authData.user.id,
          admin_email: payload.admin_email,
          admin_name: payload.admin_name,
          signup_source: 'web',
          invite_code: inviteCode,
          gdpr_consent: payload.gdpr_consent || false,
          gdpr_consent_date: payload.gdpr_consent ? new Date().toISOString() : null,
          data_processing_agreement: payload.data_processing_agreement || false,
        })
        .select()
        .single();

      if (orgError) {
        console.error('[signup] Organisation insert failed:', orgError);
        throw orgError;
      }

      // 3. Create admin employee record
      const { error: employeeError } = await adminClient
        .from('employees')
        .insert({
          org_id: orgData.id,
          email: payload.admin_email,
          name: payload.admin_name,
          password_hash: 'managed_by_supabase_auth',
          is_active: true,
          role: 'org_admin',
          auth_user_id: authData.user.id,
          signup_source: 'web',
        });

      if (employeeError) {
        console.error('[signup] Employee insert failed:', employeeError);
        throw employeeError;
      }

      console.log('[signup] Successfully created org and employee, loading user data...');

      // Reset dedup guards so loadUserData actually runs
      // (onAuthStateChange SIGNED_IN from setSession may have already grabbed them)
      loadingUserDataRef.current = false;
      lastLoadTimeRef.current = 0;

      // Load user data - sets user.userType so the signup page can redirect
      await loadUserData(authData.user);
      
      console.log('[signup] Signup completed successfully');
    } catch (err: any) {
      console.error('[signup] Signup failed:', err);
      const errorMessage = err.message || 'Signup failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (payload: LoginPayload) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: payload.email,
        password: payload.password,
      });

      if (error) {
        // Check if it's an email not confirmed error
        if (error.message.includes('Email not confirmed')) {
          throw new Error('EMAIL_NOT_VERIFIED');
        }
        throw error;
      }
      if (!data.user) throw new Error('Login failed - no user returned');

      if (data.session) {
        setSession(data.session);
        bootstrapSessionManager(data.session, null);
      }

      // Load user data
      await loadUserData(data.user);
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Logout error:', error);
      }
    } catch (err) {
      console.error('[logout] Unexpected error:', err);
    } finally {
      teardownSessionManager();
      setUser(null);
      setSession(null);
      clearRoleScope();
    }
  }, [teardownSessionManager]);

  logoutRef.current = logout;

  // Verify token
  const verifyToken = async (): Promise<boolean> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return !!session;
  };

  // Resend verification email
  const resendVerificationEmail = async (email: string) => {
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      if (error) throw error;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to resend verification email';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send password reset email';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Delete account (GDPR compliance)
  const deleteAccount = async () => {
    setError(null);
    try {
      if (!user) throw new Error('No user logged in');

      // Use admin client to bypass RLS for deletion
      const { getAdminClient } = await import('../lib/supabaseAdmin');
      const adminClient = getAdminClient();
      
      // Delete user data from database tables first
      if (user.userType === 'individual' && user.individual_data) {
        await adminClient.from('mobile_users').delete().eq('id', user.individual_data.id);
      } else if ((user.userType === 'org_admin' || user.userType === 'employee') && user.employee_data) {
        await adminClient.from('employees').delete().eq('id', user.employee_data.id);
      }
      
      // Sign out
      await logout();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete account';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Complete pending signup after email verification
  const completePendingSignup = async (): Promise<boolean> => {
    try {
      const pendingData = localStorage.getItem('pendingSignup');
      if (!pendingData) return false;

      const { type, data, user_id } = JSON.parse(pendingData);
      
      // Verify we have a session now
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || session.user.id !== user_id) {
        console.warn('[completePendingSignup] No valid session found');
        return false;
      }

      console.log('[completePendingSignup] Completing signup for:', type);

      // Use admin client to bypass RLS for signup inserts
      const { getAdminClient } = await import('../lib/supabaseAdmin');
      const adminClient = getAdminClient();

      if (type === 'org_admin') {
        // Generate a unique 8-char invite code for the organisation
        const inviteCode = Math.random().toString(36).substring(2, 10).toUpperCase();

        // Create organisation
        const { data: orgData, error: orgError } = await adminClient
          .from('organisations')
          .insert({
            name: data.org_name,
            industry: data.industry,
            size: data.company_size,
            owner_id: user_id,
            admin_email: data.admin_email,
            admin_name: data.admin_name,
            signup_source: 'web',
            invite_code: inviteCode,
            gdpr_consent: data.gdpr_consent || false,
            gdpr_consent_date: data.gdpr_consent ? new Date().toISOString() : null,
            data_processing_agreement: data.data_processing_agreement || false,
          })
          .select()
          .single();

        if (orgError) {
          console.error('[completePendingSignup] Org creation failed:', orgError);
          throw orgError;
        }

        // Create admin employee record
        const { error: employeeError } = await adminClient
          .from('employees')
          .insert({
            org_id: orgData.id,
            email: data.admin_email,
            name: data.admin_name,
            password_hash: 'managed_by_supabase_auth',
            is_active: true,
            role: 'org_admin',
            auth_user_id: user_id,
            signup_source: 'web',
          });

        if (employeeError) {
          console.error('[completePendingSignup] Employee creation failed:', employeeError);
          throw employeeError;
        }

        console.log('[completePendingSignup] Org and employee created successfully');
      } else if (type === 'individual') {
        // Create mobile_users record for individual
        const { error: userError } = await adminClient
          .from('mobile_users')
          .insert({
            email: data.email,
            name: data.name,
            phone: data.phone || null,
            auth_user_id: user_id,
            signup_source: 'web_referral',
            is_active: true,
            gdpr_consent: data.gdpr_consent || false,
            gdpr_consent_date: data.gdpr_consent ? new Date().toISOString() : null,
          });

        if (userError) {
          console.error('[completePendingSignup] Individual user creation failed:', userError);
          throw userError;
        }

        console.log('[completePendingSignup] Individual user created successfully');
      } else if (type === 'employee') {
        // Create employee record
        const { error: empError } = await adminClient
          .from('employees')
          .insert({
            org_id: data.org_id,
            email: data.email,
            name: data.name,
            password_hash: 'managed_by_supabase_auth',
            role: 'employee',
            is_active: true,
            auth_user_id: user_id,
            signup_source: 'web',
          });

        if (empError) {
          console.error('[completePendingSignup] Employee creation failed:', empError);
          throw empError;
        }

        console.log('[completePendingSignup] Employee created successfully');
      }

      // Clear pending signup data
      localStorage.removeItem('pendingSignup');

      // Reload user data
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        await loadUserData(authUser);
      }

      return true;
    } catch (err) {
      console.error('[completePendingSignup] Error:', err);
      return false;
    }
  };

  // Refresh user data from database - call after inserting records outside AuthContext
  const refreshUserData = async (): Promise<void> => {
    // Reset dedup guards - this is an explicit call so it should always run
    // (onAuthStateChange may have grabbed the lock before the DB record existed)
    loadingUserDataRef.current = false;
    lastLoadTimeRef.current = 0;

    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (authUser) {
      await loadUserData(authUser);
    }
  };

  const completeOnboarding = async (): Promise<void> => {
    if (!user) {
      console.warn('[completeOnboarding] No user logged in');
      return;
    }

    try {
      console.log('[completeOnboarding] Completing onboarding for user:', user.id, 'type:', user.userType);

      // Determine which table to update based on user type
      const table = user.userType === 'platform_admin' 
        ? 'platform_admins'
        : user.userType === 'individual' 
        ? 'mobile_users'
        : 'employees';

      // Use admin client to bypass RLS for update
      const { getAdminClient } = await import('../lib/supabaseAdmin');
      const adminClient = getAdminClient();

      const { error } = await adminClient
        .from(table)
        .update({ onboarding_complete: true })
        .eq('auth_user_id', user.id);

      if (error) {
        console.error('[completeOnboarding] Failed to update onboarding status:', error);
        throw error;
      }

      console.log('[completeOnboarding] Onboarding marked complete in', table);

      // Update local state immediately
      setUser(prev => prev ? { ...prev, onboarding_complete: true } : null);
    } catch (err) {
      console.error('[completeOnboarding] Error:', err);
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    error,
    signup,
    login,
    logout,
    verifyToken,
    resendVerificationEmail,
    resetPassword,
    deleteAccount,
    completePendingSignup,
    completeOnboarding,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
